import React from 'react';
import { Link } from 'wouter';

interface ProjectDetail {
  name: string;
  startDate: string;
  endDate: string | null;
  duration: number;
  weight: number;
  fullDuration?: number; // For showing full project duration vs intersection
  isIntersection?: boolean; // Flag to indicate if duration is intersection
}

interface LiveCalculationExampleProps {
  skillName: string;
  calculationType: 'professional' | 'activity' | 'company' | 'project' | 'private' | 'combined';
  // Fetched values
  totalProjects: number;
  projectsWithSkill: number;
  totalDuration: number;
  projects: ProjectDetail[];
  // Calculation values
  totalSkillPoints: number;
  skillSpecificPoints: number;
  finalResult: number;
  activityPeriod?: {
    start: string;
    end: string | null;
  };
  allProjectDurations?: Array<{
    name: string;
    duration: number;
  }>;
}

export default function LiveCalculationExample({ 
  skillName, 
  calculationType, 
  totalProjects,
  projectsWithSkill,
  totalDuration,
  projects,
  totalSkillPoints,
  skillSpecificPoints,
  finalResult,
  activityPeriod,
  allProjectDurations
}: LiveCalculationExampleProps) {
  // Helper function to create project slug for routing
  const createProjectSlug = (projectName: string): string => {
    return projectName.toLowerCase().replace(/ /g, '-');
  };

  const getContextLabel = () => {
    switch (calculationType) {
      case 'professional': return 'career';
      case 'activity': return 'activity';
      case 'company': return 'company';
      case 'project': return 'project';
      case 'private': return 'private projects';
      default: return 'context';
    }
  };

  return (
    <div className="space-y-4 w-full">
      <div className="bg-gray-800 border border-gray-600 p-4 rounded-lg">
        <p className="font-semibold text-sm text-blue-400 mb-4">
          Calculation for "{skillName}"
        </p>
        
        {/* Fetched Values Section */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-100 mb-3">Fetched Values:</h4>
          <div className="bg-gray-900 border border-gray-700 p-3 rounded text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-300">Skill:</span>
              <span className="text-blue-400 font-medium">{skillName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Projects in this {getContextLabel()} with the skill:</span>
              <span className="text-green-400 font-medium">{projectsWithSkill}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Total projects in this {getContextLabel()}:</span>
              <span className="text-gray-100 font-medium">{totalProjects}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Total duration of {getContextLabel()}:</span>
              <span className="text-gray-100 font-medium">{totalDuration} months</span>
            </div>
          </div>
        </div>

        {/* Projects with Skill Details */}
        {projects.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-100 mb-3">Projects with {skillName}:</h4>
            {calculationType === 'activity' && activityPeriod && (
              <div className="mb-3 p-2 bg-blue-900/30 border border-blue-700 rounded text-xs">
                <span className="text-blue-300">
                  Activity Period: {new Date(activityPeriod.start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {' '}
                  {activityPeriod.end ? new Date(activityPeriod.end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                </span>
              </div>
            )}
            <div className="space-y-3">
              {projects.map((project, index) => (
                <div key={index} className="bg-gray-900 border border-gray-700 p-3 rounded">
                  <div className="font-medium text-blue-400 mb-2">
                    Project {index + 1} - "
                    <Link href={`/project/${createProjectSlug(project.name)}`}>
                      <span className="hover:underline cursor-pointer">
                        {project.name}
                      </span>
                    </Link>
                    "
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    <span className="text-gray-300">Project Period: </span>
                    {new Date(project.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {' '}
                    {project.endDate ? new Date(project.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">
                        {project.isIntersection ? 'Duration in activity:' : 'Duration:'}
                      </span>
                      <span className="text-gray-100">{project.duration} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Weight of skill:</span>
                      <span className="text-green-400">{Math.round(project.weight * 100)}%</span>
                    </div>
                  </div>
                  {project.isIntersection && project.fullDuration && (
                    <div className="mt-2 p-2 bg-yellow-900/30 border border-yellow-700 rounded text-xs">
                      <span className="text-yellow-300">
                        Note: Full project duration is {project.fullDuration} months, but only {project.duration} months overlap with this activity period.
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calculation Section */}
        <div className="mb-4">
          <h4 className="font-semibold text-gray-100 mb-3">Calculation:</h4>
          
          {/* Total Skill Points */}
          <div className="bg-gray-900 border border-gray-700 p-3 rounded mb-3">
            <div className="font-medium text-orange-400 mb-2">Total Skill Points:</div>
            
            {/* Show all project durations breakdown if available */}
            {allProjectDurations && allProjectDurations.length > 0 && (
              <div className="text-sm mb-3 font-mono">
                {/* Header row */}
                <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 mb-2 py-1 border-b border-gray-700">
                  <span className="text-gray-400 font-semibold">Project Title</span>
                  <span className="text-gray-400 font-semibold text-center">Formula</span>
                  <span className="text-gray-400 font-semibold text-right">Result</span>
                </div>
                
                {/* Project rows */}
                {allProjectDurations.map((project, index) => (
                  <div key={index} className="grid grid-cols-[2fr_1fr_1fr] gap-2 mb-1 py-1">
                    <span className="text-gray-300 truncate">
                      <Link href={`/project/${createProjectSlug(project.name)}`}>
                        <span className="hover:underline cursor-pointer">
                          {project.name}
                        </span>
                      </Link>
                    </span>
                    <span className="text-blue-300 text-center">
                      {Math.round(project.duration * 10) / 10} months × 100%
                    </span>
                    <span className="text-blue-400 font-bold text-right">
                      {Math.round(project.duration * 10) / 10} points
                    </span>
                  </div>
                ))}
                
                {/* Addition line */}
                <div className="border-t border-gray-600 mt-2 pt-2 flex justify-between items-center">
                  <span className="text-gray-300 font-semibold">
                    = {calculationType === 'company' 
                        ? 'Sum of all company project durations' 
                        : `Sum of all ${getContextLabel()} project durations`}
                  </span>
                  <span className="text-orange-400 font-bold">
                    = {totalSkillPoints} points
                  </span>
                </div>
              </div>
            )}
            
            {/* Fallback when no project breakdown is available */}
            {(!allProjectDurations || allProjectDurations.length === 0) && (
              <>
                <div className="text-sm text-gray-300 mb-1">
                  = {calculationType === 'company' 
                      ? 'Sum of all company project durations' 
                      : `Sum of all ${getContextLabel()} project durations`}
                </div>
                <div className="text-right text-orange-400 font-bold">
                  = {totalSkillPoints} points
                </div>
              </>
            )}
          </div>

          {/* Skill-Specific Points */}
          <div className="bg-gray-900 border border-gray-700 p-3 rounded mb-3">
            <div className="font-medium text-purple-400 mb-2">{skillName} Skill Points:</div>
            
            {/* Show detailed breakdown for each project */}
            <div className="text-sm mb-3 font-mono">
              {/* Header row */}
              <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 mb-2 py-1 border-b border-gray-700">
                <span className="text-gray-400 font-semibold">Project Title</span>
                <span className="text-gray-400 font-semibold text-center">Formula</span>
                <span className="text-gray-400 font-semibold text-right">Result</span>
              </div>
              
              {/* Project rows */}
              {projects.map((project, index) => (
                <div key={index} className="grid grid-cols-[2fr_1fr_1fr] gap-2 mb-1 py-1">
                  <span className="text-gray-300 truncate">
                    <Link href={`/project/${createProjectSlug(project.name)}`}>
                      <span className="hover:underline cursor-pointer">
                        {project.name}
                      </span>
                    </Link>
                  </span>
                  <span className="text-purple-300 text-center">
                    {Math.round(project.duration * 10) / 10} months × {Math.round(project.weight * 100)}%
                  </span>
                  <span className="text-purple-400 font-bold text-right">
                    {(project.duration * project.weight).toFixed(1)} points
                  </span>
                </div>
              ))}
              
              {/* Addition line */}
              <div className="border-t border-gray-600 mt-2 pt-2 flex justify-between items-center">
                <span className="text-gray-300 font-semibold">
                  = Total {skillName} points
                </span>
                <span className="text-purple-400 font-bold">
                  = {skillSpecificPoints.toFixed(1)} points
                </span>
              </div>
            </div>
          </div>

          {/* Final Calculation */}
          <div className="bg-green-900 border border-green-700 p-4 rounded">
            <div className="font-medium text-green-400 mb-3">Final Skill Level:</div>
            
            {/* Fraction with horizontal continuation */}
            <div className="bg-gray-800 border border-gray-600 p-4 rounded font-mono">
              <div className="flex items-center justify-center gap-3">
                {/* Vertical fraction */}
                <div className="flex flex-col items-center">
                  <div className="text-purple-400 font-semibold text-lg">
                    {skillSpecificPoints.toFixed(1)} skill points
                  </div>
                  <div className="w-48 h-px bg-gray-400 my-1"></div>
                  <div className="text-orange-400 font-semibold text-lg">
                    {totalSkillPoints} total points
                  </div>
                </div>
                
                {/* Continuation of formula */}
                <div className="text-gray-300 text-lg">
                  × 100% =
                </div>
                
                {/* Final result */}
                <div className="text-green-400 font-bold text-xl bg-green-900/50 px-3 py-1 rounded border border-green-700">
                  {finalResult}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
